import { useState } from 'react';

import { Card } from '@components/card';
import { useCreateFormStore } from '@components/form';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import { TravellerAttributeTemplateData } from 'api/attributes/requests';

import {
  DetailsSection,
  FieldFormData,
  FieldFormSection,
  FieldFormWithUnsavedChangesPrompt,
  Form,
  FormFooter,
  applyInputStatus,
  locale,
} from './field-form-shared';

import { fieldTypeConfig } from '../common';

type NumberFieldFormProps = {
  attribute?: TravellerAttributeTemplateData;
  isTravellerAttribute: boolean;
  onSubmit: (data: FieldFormData) => Promise<boolean>;
  isEditing: boolean;
};

export const NumberFieldForm = ({
  attribute,
  isTravellerAttribute,
  onSubmit,
  isEditing,
}: NumberFieldFormProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const formValues = formStore.useFormValues();
  const isDecimal = formValues.isDecimal as boolean;

  const [chosenType, setChosenType] = useState<'number' | 'decimal'>(
    attribute?.attributeType === 'decimal' ? 'decimal' : 'number',
  );

  const handleSubmit = async (data: FieldFormData) => {
    const submitData = applyInputStatus(
      { ...data, attributeType: chosenType },
      isTravellerAttribute,
    );
    return onSubmit(submitData);
  };

  return (
    <FieldFormWithUnsavedChangesPrompt onSubmit={handleSubmit} store={formStore}>
      <Stack space={6}>
        <DetailsSection
          attributeType={chosenType}
          attribute={attribute}
          isTravellerAttribute={isTravellerAttribute}
        />

        <Sidebar.Separator />

        <FieldFormSection label={t('formatting')}>
          <Stack space={4}>
            <Stack space={1} orientation="horizontal" align="center" justify="space-between">
              <Text font="regular14" text={t('allowDecimalNumbers')} />
              <Form.Switch
                // @ts-ignore
                id="isDecimal"
                label={t('allowDecimalNumbers')}
                labelVisible={false}
                defaultValue={attribute?.attributeType === 'decimal'}
                onChange={(value) => {
                  setChosenType(value ? 'decimal' : 'number');
                }}
                isDisabled={isEditing}
              />
            </Stack>
            <NumberExampleCard isDecimal={isDecimal ?? false} />
          </Stack>
        </FieldFormSection>
      </Stack>
      <FormFooter isEditing={isEditing} isDirty={isDirty} />
    </FieldFormWithUnsavedChangesPrompt>
  );
};

const NumberExampleCard = ({ isDecimal }: { isDecimal: boolean }) => {
  const { t } = useScopedTranslate(locale);
  const number = isDecimal ? '3,456.00' : '3,456,000';
  const Icon = isDecimal ? fieldTypeConfig.decimal.icon : fieldTypeConfig.number.icon;

  return (
    <Card space={3} variant="transparent" style={{ background: theme.color.indigoTrans5 }}>
      <Stack space={2} orientation="horizontal" align="center" justify="space-between">
        <Stack space={2} orientation="horizontal" align="center">
          <Icon color="indigo200" />
          <Text font="semibold14" color="indigo100" text={number} />
        </Stack>
        <Text font="semibold12" color="textSecondary" text={t('example')} />
      </Stack>
    </Card>
  );
};
